import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import {
  getBuyerTarget,
  getCampaignObjection,
  getCampaignTarget,
  getL0Objectives,
  getL1Objectives,
  getCampaignCluster,
  PostCampaignUmbrellaRequestType,
  getCampaignUmbrellaDetail,
  postCampaignUmbrella,
  putCampaignUmbrella,
  PutCampaignUmbrellaRequestType,
  getPackages,
} from 'features/CampaignAndDiscount/services/campaignUmbrella'
import { toastFailed, toastSuccess } from 'utils/toast'
import { getFileRules, FileVariantIdType, postFile } from 'utils/apiList/files'
import { SLICE_NAME, setOptions, setIsLoading, setForms, setFileRule } from './slice'

export const fetchCampaignOptions = createAsyncThunk(
  `${SLICE_NAME}/fetchCampaignOptions`,
  async (_, { dispatch, rejectWithValue, getState }) => {
    const {
      campaignUmbrellaAddEdit: { options },
    } = getState() as StoreStateType

    dispatch(
      setOptions({
        campaignObjectives: { isLoading: false, data: options.campaignObjectives.data },
        buyerTarget: { isLoading: false, data: options.buyerTarget.data },
        l0Objectives: { isLoading: false, data: options.l0Objectives.data },
        l1Objectives: { isLoading: false, data: options.l1Objectives.data },
        campaignTarget: { isLoading: false, data: options.campaignTarget.data },
      }),
    )
    try {
      const [
        campaignObjectives,
        buyerTarget,
        l0Objectives,
        l1Objectives,
        campaignTarget,
        campaignCluster,
        packages,
      ] = await Promise.all([
        getCampaignObjection(),
        getBuyerTarget(),
        getL0Objectives(),
        getL1Objectives(),
        getCampaignTarget(),
        getCampaignCluster(),
        getPackages(),
      ])

      dispatch(
        setOptions({
          campaignObjectives: { isLoading: false, data: campaignObjectives.data.data },
          buyerTarget: { isLoading: false, data: buyerTarget.data.data },
          l0Objectives: { isLoading: false, data: l0Objectives.data.data },
          l1Objectives: { isLoading: false, data: l1Objectives.data.data },
          campaignTarget: { isLoading: false, data: campaignTarget.data.data },
          cluster: {
            isLoading: false,
            data: campaignCluster.data.data.campaign_clusters.map((item) => ({
              id: item.id.toString(),
              name: item.cluster,
            })),
          },
          packages: { isLoading: false, data: packages.data.data },
        }),
      )

      return {
        campaignObjectives: campaignObjectives.data,
        buyerTarget: buyerTarget.data,
        l0Objectives: l0Objectives.data,
        l1Objectives: l1Objectives.data,
        campaignTarget: campaignTarget.data,
      }
    } catch (error) {
      dispatch(setIsLoading(false))
      callErrorMsg(error)

      return rejectWithValue(error)
    }
  },
)

export const createCampaignUmbrella = createAsyncThunk(
  `${SLICE_NAME}/createCampaignUmbrella`,
  async (payload: PostCampaignUmbrellaRequestType['data'], { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))
    try {
      const { data } = await postCampaignUmbrella({ data: payload })

      dispatch(setIsLoading(false))
      if (data.error.status) {
        toastFailed(data.error.message)
      }
      toastSuccess(data.message)
      return data
    } catch (error) {
      dispatch(setIsLoading(false))
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const updateCampaignUmbrella = createAsyncThunk(
  `${SLICE_NAME}/updateCampaignUmbrella`,
  async (payload: PutCampaignUmbrellaRequestType, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))
    try {
      const { data } = await putCampaignUmbrella(payload) // Assuming putCampaignUmbrella is defined in services

      dispatch(setIsLoading(false))
      if (data.error.status) {
        toastFailed(data.error.message)
      }
      toastSuccess(data.message)
      return data
    } catch (error) {
      dispatch(setIsLoading(false))
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchCampaignUmbrellaDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchCampaignUmbrellaDetail`,
  async ({ brandSubmissionID }: { brandSubmissionID: string }, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))

    try {
      const { data: dateRes } = await getCampaignUmbrellaDetail({ brandSubmissionID })

      const { error, data } = dateRes
      if (error.status) {
        toastFailed(error.message)
      } else {
        dispatch(setForms(data))
      }
      dispatch(setIsLoading(false))

      return dateRes
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return rejectWithValue(error)
    }
  },
)

export const fetchFileRules = createAsyncThunk(
  `${SLICE_NAME}/fetchFileRules`,
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await getFileRules('skp-brand-submission-attachment')

      if (!data.error.status) {
        dispatch(setFileRule(data.data))
      }

      dispatch(setIsLoading(false))
      return data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return rejectWithValue(error)
    }
  },
)

export const uploadFile = createAsyncThunk(
  `${SLICE_NAME}/uploadFile`,
  async (file: File, { rejectWithValue, dispatch }) => {
    dispatch(setIsLoading(true))

    try {
      const payload = {
        typeId: 'skp-brand-submission-attachment' as FileVariantIdType,
        data: { file_input: file },
      }
      const response = await postFile(payload)

      dispatch(setIsLoading(false))
      return response.data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return rejectWithValue(error)
    }
  },
)
